/** PageSingle component - render a basic static Page from Contentful */
import { Grid, Typography, Box } from '@material-ui/core'
import CalendarIcon from '@material-ui/icons/CalendarToday'
import PersonIcon from '@material-ui/icons/Person'
import { GetStaticProps, GetStaticPaths } from 'next'
import React from 'react'

import Link from '@aletheia/common/components/Link'
import Markdown from '@aletheia/common/components/Markdown'
import PageComponent from '@aletheia/common/components/Page'
import PrettyDate from '@aletheia/common/components/__legacy/utilities/PrettyDate'

import { IPost, fetchPost, fetchPosts, isEntry } from '../../contentful'

type StaticPostProps = {
  Post: IPost
}

const PostSingle: React.FC<StaticPostProps> = ({ Post }) => {
  const { title, authors, body, bannerImage, datePublished } = Post.fields
  return (
    <PageComponent
      title={title}
      centerTitle
      banner={bannerImage}
      variant="narrow"
      breadcrumbsBase={{ href: '/blog', label: 'Blog' }}
    >
      <Box marginBottom={3}>
        <Grid container spacing={3}>
          <Grid item>
            <Typography>
              <PersonIcon />{' '}
              {authors.filter(isEntry).map((author, index) => (
                <React.Fragment key={author.sys.id}>
                  {index > 0 &&
                    (index === authors.length - 1 ? <> &amp; </> : <>, </>)}
                  <Link href={`/people/${author.fields.slug}`}>
                    {author.fields.title}
                  </Link>
                </React.Fragment>
              ))}
            </Typography>
          </Grid>
          <Grid item>
            <Typography>
              <CalendarIcon /> <PrettyDate value={datePublished} />
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Markdown>{body}</Markdown>
    </PageComponent>
  )
}

export default PostSingle

export const getStaticProps: GetStaticProps<
  StaticPostProps,
  { slug: string }
> = async ({ params }) => {
  if (!params?.slug) throw new Error('Missing slug')
  const Post = await fetchPost(params.slug)
  // should never happen because fallback=false below, but let's keep TS happy
  if (!Post) throw new Error('No Page')
  return {
    props: { Post },
    revalidate: 1 * 60,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const Posts = await fetchPosts()
  return {
    paths: Posts.map((Post) => ({
      params: { slug: Post.fields.slug },
    })),
    fallback: false,
  }
}
